// ============================================================
// /aliados/demo (antes /influencer/demo) · PROTOTIPO DEL PANEL DE INFLUENCERS (s218)
//
// Pedido: claude/influencer/PEDIDO_CODE_prototipo_panel_influencer.md (Cowork, s217).
// Otto lo abre en el teléfono frente a una influencer el sábado 10/10: se ve y se siente como el
// producto, con DATOS DE EJEMPLO. No llama a Bubble ni a Stripe. La versión real (/influencer) se
// cablea sobre este diseño con el contrato de claude/influencer/CONTRATO_s218_panel_influencer_para_code.md.
//
// Reglas: ningún botón da error (lo que no funciona dice «Disponible pronto»); miembros sin nombre;
// montos con decimales y verosímiles (1-3 meses, decenas de miembros). Estilos en influencer.css.
// ============================================================
import React, { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";

// ---- Datos de ejemplo ------------------------------------------------------
// Precios vigentes de la membresía (s193-D21): $12.99/mes y $119.99/año.
const DEMO = {
  nombre: "Valentina",
  codigo: "VALEMIAMI",
  // La puerta real del canal es /i/:codigo (InfluencerLandingPage): no valida el código, así que el QR del demo
  // lleva a una página que funciona aunque VALEMIAMI no exista.
  link: "https://app.nxtreserve.com/i/VALEMIAMI",
  condiciones: { pct: 20, meses: 12, retencion: 15 },   // s220 · los términos de Ali: 20 %, día 15, cobros con 15 días
  proximoPago: "15 de octubre",
  aCobrar: 226.45,       // listo para cobrar en el próximo pago
  enRetencion: 173.8,    // se libera cuando pasen los días de retención
  pagado: 502.28,        // lo que ya se pagó (suma del historial)
  meta: { actual: 42, objetivo: 50, pctSiguiente: 30 },
  embudo: { registrados: 86, prueba: 11, pagando: 42, cancelados: 9 },
  semanas: [
    { r: "10 ago", n: 2 }, { r: "17 ago", n: 4 }, { r: "24 ago", n: 5 }, { r: "31 ago", n: 7 },
    { r: "7 sep", n: 9 }, { r: "14 sep", n: 11 }, { r: "21 sep", n: 12 }, { r: "28 sep", n: 14 },
    { r: "5 oct", n: 13 }, { r: "hoy", n: 9 },
  ],
  meses: [{ r: "Agosto", n: 21 }, { r: "Septiembre", n: 44 }, { r: "Octubre", n: 21 }],
  pagos: [
    // s220 · un pago al mes, el día 15 (antes eran quincenales). Lo pagado sigue sumando 502.28.
    { periodo: "Cobros de septiembre", monto: 226.45, metodo: "Zelle", estado: "programado", fecha: "15 oct" },
    { periodo: "Cobros de agosto", monto: 339.93, metodo: "Zelle", estado: "pagado", fecha: "15 sep" },
    { periodo: "Cobros de julio", monto: 162.35, metodo: "PayPal", estado: "pagado", fecha: "15 ago" },
  ],
  ofertas: [
    { rubro: "Restaurante · Brickell", frase: "Ahorra $20", cond: "cuando gastas $40 en la cena" },
    { rubro: "Spa · Coral Gables", frase: "Ahorra $25", cond: "cuando gastas $50 en tu masaje" },
    { rubro: "Rooftop · Wynwood", frase: "Ahorra $15", cond: "cuando gastas $30 en el happy hour" },
  ],
};

// 62 miembros anónimos (42 pagando · 11 en prueba · 9 cancelados), del más nuevo al más viejo. Determinístico:
// la misma lista en cada recarga, para que Otto no vea números distintos frente a la influencer.
function miembrosDeEjemplo() {
  const estados = [...Array(11).fill("prueba"), ...Array(42).fill("activo"), ...Array(9).fill("cancelado")];
  const dia0 = new Date(2026, 9, 7);   // 7 de octubre
  let semilla = 7;
  const azar = () => { semilla = (semilla * 9301 + 49297) % 233280; return semilla / 233280; };
  return estados.map((estado, i) => {
    // Los de prueba entraron en la última semana; los demás, repartidos en ~9 semanas.
    const hace = estado === "prueba" ? Math.floor(azar() * 7) : 7 + Math.floor(azar() * 58);
    const d = new Date(dia0); d.setDate(d.getDate() - hace);
    return { n: 62 - i, estado, plan: azar() < 0.3 ? "Anual" : "Mensual", entro: d };
  }).sort((a, b) => b.entro - a.entro).map((m, i, todos) => ({ ...m, n: todos.length - i }));
}

const dinero = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const fechaCorta = (d) => d.toLocaleDateString("es-US", { day: "numeric", month: "short" });

// ---- Iconos (familia del sistema: 24x24, trazo 1.5, currentColor) ----------
const Ico = ({ d, size = 20 }) => (
  <svg className={`ico ico-${size}`} viewBox="0 0 24 24" aria-hidden="true"><path d={d} /></svg>
);
const IC = {
  copiar: "M8 8h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM5 16V5a1 1 0 0 1 1-1h11",
  bajar: "M12 4v11m0 0-4-4m4 4 4-4M5 19h14",
  listo: "M5 12.5 10 17l9-10",
  compartir: "M12 4v11M8 8l4-4 4 4M6 13v5a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-5",
  candado: "M7 11V8a5 5 0 0 1 10 0v3M6 11h12v9H6z",
};

// ---- Piezas -----------------------------------------------------------------
function useAviso() {
  const [texto, setTexto] = useState("");
  const t = useRef(null);
  const mostrar = (s) => { setTexto(s); clearTimeout(t.current); t.current = setTimeout(() => setTexto(""), 2200); };
  useEffect(() => () => clearTimeout(t.current), []);
  return [texto, mostrar];
}
const pronto = (avisar) => () => avisar("Disponible pronto");

function copiarTexto(texto) {
  try {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(texto).then(() => true, () => false);
  } catch (e) { /* sigue abajo */ }
  return Promise.resolve(false);
}

function BotonCopiar({ texto, etiqueta = "Copiar", onHecho }) {
  const [ok, setOk] = useState(false);
  return (
    <button type="button" className="inf-btn inf-btn--linea" onClick={async () => {
      const hecho = await copiarTexto(texto);
      setOk(true); setTimeout(() => setOk(false), 1600);
      if (onHecho) onHecho(hecho ? "Copiado" : "Mantén presionado el texto para copiarlo");
    }}>
      <Ico d={ok ? IC.listo : IC.copiar} />{ok ? "Copiado" : etiqueta}
    </button>
  );
}

// ---- Resumen -------------------------------------------------------------------
function Resumen({ avisar }) {
  const { aCobrar, enRetencion, pagado, condiciones: c, meta, embudo: e } = DEMO;
  const total = aCobrar + enRetencion + pagado;
  const [vista, setVista] = useState("semana");
  const serie = vista === "semana" ? DEMO.semanas : DEMO.meses;
  const max = Math.max(...serie.map(s => s.n));
  const faltan = meta.objetivo - meta.actual;
  const pasos = [
    { k: "Se registraron con tu código", n: e.registrados },
    { k: "Están en su prueba de 7 días", n: e.prueba },
    { k: "Pagan su membresía", n: e.pagando },
    { k: "Cancelaron", n: e.cancelados },
  ];
  return (
    <>
      <section className="inf-bloque" aria-labelledby="inf-dinero">
        <h2 id="inf-dinero" className="inf-h2">Tu dinero</h2>
        <div className="inf-barra" role="img"
          aria-label={`Pagado ${dinero(pagado)}, listo para cobrar ${dinero(aCobrar)}, en retención ${dinero(enRetencion)}`}>
          <span className="inf-barra__tramo inf-barra__tramo--pagado" style={{ flexGrow: pagado / total }} />
          <span className="inf-barra__tramo inf-barra__tramo--listo" style={{ flexGrow: aCobrar / total }} />
          <span className="inf-barra__tramo inf-barra__tramo--retenido" style={{ flexGrow: enRetencion / total }} />
        </div>
        <dl className="inf-leyenda">
          <div><dt><i className="inf-punto inf-punto--pagado" />Pagado</dt><dd>{dinero(pagado)}</dd></div>
          <div><dt><i className="inf-punto inf-punto--listo" />Listo para cobrar</dt><dd>{dinero(aCobrar)}</dd></div>
          <div><dt><i className="inf-punto inf-punto--retenido" />En retención</dt><dd>{dinero(enRetencion)}</dd></div>
        </dl>
        <p className="inf-nota">
          Lo que está en retención se libera a los {c.retencion} días del pago del miembro, por si hay un reembolso, y se cobra el día 15.
        </p>
      </section>

      <section className="inf-bloque" aria-labelledby="inf-cond">
        <h2 id="inf-cond" className="inf-h2">Tus condiciones</h2>
        <p className="inf-condiciones">
          <b>{c.pct} %</b> de cada pago de tus miembros, <b>durante {c.meses} meses</b>. Se paga el <b>día 15</b> de cada mes, por los cobros que ya tienen <b>{c.retencion} días</b> y no se reembolsaron.
        </p>
        <button type="button" className="inf-enlace" onClick={pronto(avisar)}>Ver tu contrato</button>
      </section>

      <section className="inf-bloque inf-meta" aria-labelledby="inf-meta">
        <h2 id="inf-meta" className="inf-h2">Tu próxima meta</h2>
        <p className="inf-meta__texto">Te faltan <b>{faltan} miembros activos</b> para subir a <b>{meta.pctSiguiente} %</b>.</p>
        <div className="inf-progreso" role="progressbar" aria-valuemin={0} aria-valuemax={meta.objetivo} aria-valuenow={meta.actual}
          aria-label={`${meta.actual} de ${meta.objetivo} miembros activos`}>
          <span style={{ width: `${(meta.actual / meta.objetivo) * 100}%` }} />
        </div>
        <p className="inf-nota">{meta.actual} de {meta.objetivo}</p>
      </section>

      <section className="inf-bloque" aria-labelledby="inf-embudo">
        <h2 id="inf-embudo" className="inf-h2">Cómo va tu código</h2>
        <ol className="inf-embudo">
          {pasos.map(p => (
            <li key={p.k} className="inf-embudo__paso">
              <span className="inf-embudo__n">{p.n}</span>
              <span className="inf-embudo__k">{p.k}</span>
              <span className="inf-embudo__barra" aria-hidden="true"><span style={{ width: `${(p.n / e.registrados) * 100}%` }} /></span>
            </li>
          ))}
        </ol>
      </section>

      <section className="inf-bloque" aria-labelledby="inf-tend">
        <div className="inf-cabeza">
          <h2 id="inf-tend" className="inf-h2">Registros con tu código</h2>
          <div className="inf-segmento" role="group" aria-label="Agrupar por">
            {[["semana", "Por semana"], ["mes", "Por mes"]].map(([k, t]) => (
              <button key={k} type="button" aria-pressed={vista === k} onClick={() => setVista(k)}>{t}</button>
            ))}
          </div>
        </div>
        <div className={`inf-grafico inf-grafico--${vista}`} role="img"
          aria-label={serie.map(s => `${s.r}: ${s.n}`).join(", ")}>
          {serie.map((s, i) => (
            <div key={s.r} className="inf-grafico__col">
              <span className="inf-grafico__n">{s.n}</span>
              <span className={`inf-grafico__barra${i === serie.length - 1 ? " inf-grafico__barra--ahora" : ""}`} style={{ height: `${(s.n / max) * 100}%` }} />
              <span className="inf-grafico__r">{s.r}</span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

// ---- Miembros ------------------------------------------------------------------
const ESTADO_MIEMBRO = { activo: "Activo", prueba: "En prueba", cancelado: "Canceló" };
function Miembros() {
  const todos = useMemo(miembrosDeEjemplo, []);
  const [filtro, setFiltro] = useState("todos");
  const [cuantos, setCuantos] = useState(15);
  const lista = filtro === "todos" ? todos : todos.filter(m => m.estado === filtro);
  const cuenta = (k) => (k === "todos" ? todos.length : todos.filter(m => m.estado === k).length);
  return (
    <section className="inf-bloque" aria-labelledby="inf-miembros">
      <h2 id="inf-miembros" className="inf-h2">Tus miembros</h2>
      <p className="inf-nota">Sin nombres: por privacidad ves el número de miembro, cuándo entró y su plan.</p>
      <div className="inf-chips" role="group" aria-label="Filtrar por estado">
        {[["todos", "Todos"], ["activo", "Activos"], ["prueba", "En prueba"], ["cancelado", "Cancelaron"]].map(([k, t]) => (
          <button key={k} type="button" className="inf-chip" aria-pressed={filtro === k} onClick={() => { setFiltro(k); setCuantos(15); }}>
            {t} <span className="inf-chip__n">{cuenta(k)}</span>
          </button>
        ))}
      </div>
      <ul className="inf-lista">
        {lista.slice(0, cuantos).map(m => (
          <li key={m.n} className="inf-fila">
            <span className="inf-fila__n">#{m.n}</span>
            <span className="inf-fila__datos">
              <span>Entró el {fechaCorta(m.entro)}</span>
              <span className="inf-fila__plan">{m.plan}</span>
            </span>
            <span className={`inf-estado inf-estado--${m.estado}`}>{ESTADO_MIEMBRO[m.estado]}</span>
          </li>
        ))}
      </ul>
      {lista.length > cuantos && (
        <button type="button" className="inf-btn inf-btn--linea inf-btn--ancho" onClick={() => setCuantos(c => c + 15)}>
          Ver más ({lista.length - cuantos})
        </button>
      )}
    </section>
  );
}

// ---- Pagos ---------------------------------------------------------------------
function Pagos({ avisar }) {
  return (
    <>
      <section className="inf-aviso" aria-labelledby="inf-w9">
        <h2 id="inf-w9" className="inf-aviso__titulo">Necesitamos tu W-9 antes del primer pago</h2>
        <p>Es el formulario del IRS con tus datos de contribuyente. Lo subes una vez y queda guardado.</p>
        <button type="button" className="inf-btn inf-btn--accion" onClick={pronto(avisar)}>Subir mi W-9</button>
      </section>
      <section className="inf-bloque" aria-labelledby="inf-pagos">
        <h2 id="inf-pagos" className="inf-h2">Tus pagos</h2>
        <ul className="inf-lista">
          {DEMO.pagos.map(p => (
            <li key={p.periodo} className="inf-fila inf-fila--pago">
              <span className="inf-fila__datos">
                <span className="inf-fila__titulo">{p.periodo}</span>
                <span className="inf-fila__plan">{p.metodo} · {p.estado === "pagado" ? `pagado el ${p.fecha}` : `se paga el ${p.fecha}`}</span>
              </span>
              <span className="inf-fila__monto">{dinero(p.monto)}</span>
              <span className={`inf-estado inf-estado--${p.estado === "pagado" ? "activo" : "prueba"}`}>{p.estado === "pagado" ? "Pagado" : "Programado"}</span>
            </li>
          ))}
        </ul>
        <div className="inf-metodo">
          <span>Cobras por <b>Zelle</b> a tu número terminado en 0118.</span>
          <button type="button" className="inf-enlace" onClick={pronto(avisar)}>Cambiar</button>
        </div>
      </section>
    </>
  );
}

// ---- Para vender ---------------------------------------------------------------
const TEXTOS = [
  "Encontré la forma de salir en Miami gastando menos: con NXT Reserve tienes beneficios en restaurantes, spas y rooftops. Entra con mi código VALEMIAMI y prueba 7 días. #publicidad",
  "¿Plan de viernes? Yo ya uso NXT Reserve: llegas, muestras tu código y ahorras en la cuenta. Con VALEMIAMI tienes tu prueba de 7 días. #publicidad",
];
function ParaVender({ avisar }) {
  const [qr, setQr] = useState("");
  useEffect(() => {
    let vivo = true;
    QRCode.toDataURL(DEMO.link, { margin: 2, width: 560, errorCorrectionLevel: "M", color: { dark: "#0F1B2A", light: "#FFFFFF" } })
      .then(u => { if (vivo) setQr(u); }).catch(() => {});
    return () => { vivo = false; };
  }, []);
  return (
    <>
      <section className="inf-bloque" aria-labelledby="inf-link">
        <h2 id="inf-link" className="inf-h2">Tu link y tu código</h2>
        <div className="inf-campo">
          <span className="inf-campo__valor">{DEMO.link.replace("https://", "")}</span>
          <BotonCopiar texto={DEMO.link} onHecho={avisar} />
        </div>
        <div className="inf-campo">
          <span className="inf-campo__valor inf-campo__valor--codigo">{DEMO.codigo}</span>
          <BotonCopiar texto={DEMO.codigo} onHecho={avisar} />
        </div>
        <div className="inf-qr">
          {qr ? <img src={qr} alt={`Código QR de ${DEMO.link}`} width="168" height="168" /> : <span className="inf-qr__vacio" />}
          <div>
            <p className="inf-qr__texto">Ponlo en tus historias o imprímelo: lleva directo a tu link.</p>
            <a className={`inf-btn inf-btn--linea${qr ? "" : " inf-btn--apagado"}`} href={qr || undefined} download={`QR-${DEMO.codigo}.png`}
              aria-disabled={!qr} onClick={e => { if (!qr) e.preventDefault(); }}>
              <Ico d={IC.bajar} />Descargar QR
            </a>
          </div>
        </div>
      </section>

      <section className="inf-bloque" aria-labelledby="inf-kit">
        <h2 id="inf-kit" className="inf-h2">Para tus historias</h2>
        <p className="inf-nota">Las ofertas de la semana, listas para publicar. Cambian cada lunes.</p>
        <div className="inf-historias">
          {DEMO.ofertas.map(o => (
            <figure key={o.rubro} className="inf-historia">
              <div className="inf-historia__arte">
                <span className="inf-historia__marca">NXT RESERVE</span>
                <span className="inf-historia__frase">{o.frase}</span>
                <span className="inf-historia__cond">{o.cond}</span>
                <span className="inf-historia__codigo">Código {DEMO.codigo}</span>
                <span className="inf-historia__pub">#publicidad</span>
              </div>
              <figcaption>{o.rubro}</figcaption>
              <button type="button" className="inf-btn inf-btn--linea inf-btn--ancho" onClick={pronto(avisar)}>
                <Ico d={IC.compartir} />Compartir
              </button>
            </figure>
          ))}
        </div>
      </section>

      <section className="inf-bloque" aria-labelledby="inf-textos">
        <h2 id="inf-textos" className="inf-h2">Textos sugeridos</h2>
        <p className="inf-nota">Ya traen <b>#publicidad</b>: la ley de EE. UU. (FTC) pide decir que es contenido pagado.</p>
        {TEXTOS.map((t, i) => (
          <div key={i} className="inf-texto">
            <p>{t}</p>
            <BotonCopiar texto={t} etiqueta="Copiar texto" onHecho={avisar} />
          </div>
        ))}
      </section>
    </>
  );
}

// ---- «Crea tu contraseña» (solo visual) -------------------------------------
function PrimeraVez({ onVolver, avisar }) {
  return (
    <section className="inf-puerta" aria-labelledby="inf-puerta">
      <span className="inf-puerta__ico"><Ico d={IC.candado} size={32} /></span>
      <h2 id="inf-puerta" className="inf-puerta__titulo">Crea tu contraseña</h2>
      <p className="inf-puerta__texto">
        Así entras la primera vez: te llega un correo de NXT Reserve con un enlace. Lo abres, eliges tu contraseña y ya ves tu panel.
      </p>
      <form className="inf-puerta__form" onSubmit={e => { e.preventDefault(); avisar("Disponible pronto"); }}>
        <label>Tu correo<input type="email" value="tucorreo@ejemplo.com" readOnly /></label>
        <label>Contraseña nueva<input type="password" placeholder="Mínimo 8 caracteres" autoComplete="off" /></label>
        <button type="submit" className="inf-btn inf-btn--accion inf-btn--ancho">Crear mi contraseña</button>
      </form>
      <button type="button" className="inf-enlace" onClick={onVolver}>Volver al panel</button>
    </section>
  );
}

// ---- La página -------------------------------------------------------------------
const PESTANAS = [["resumen", "Resumen"], ["miembros", "Miembros"], ["pagos", "Pagos"], ["vender", "Para vender"]];

export default function InfluencerDemo() {
  const [pestana, setPestana] = useState("resumen");
  const [puerta, setPuerta] = useState(false);
  const [aviso, avisar] = useAviso();
  useEffect(() => { document.title = "Tu panel · NXT Reserve (demo)"; }, []);
  const ir = (k) => { setPestana(k); setPuerta(false); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="inf">
      <div className="inf__banda" role="note">Versión beta · datos de ejemplo</div>

      <header className="inf-cabecera">
        <div className="inf-cabecera__fila">
          <span className="inf-cabecera__marca">NXT RESERVE</span>
          <span className="inf-cabecera__codigo">{DEMO.codigo}</span>
        </div>
        <p className="inf-cabecera__hola">Hola, {DEMO.nombre}. Este mes vas a cobrar</p>
        <p className="inf-cabecera__cifra">{dinero(DEMO.aCobrar)}</p>
        <p className="inf-cabecera__fecha">Próximo pago: {DEMO.proximoPago}, por Zelle</p>
      </header>

      <nav className="inf-pestanas" aria-label="Secciones del panel">
        {PESTANAS.map(([k, t]) => (
          <button key={k} type="button" aria-current={!puerta && pestana === k ? "page" : undefined} onClick={() => ir(k)}>{t}</button>
        ))}
      </nav>

      <main className="inf__columna">
        {puerta ? <PrimeraVez onVolver={() => setPuerta(false)} avisar={avisar} />
          : pestana === "resumen" ? <Resumen avisar={avisar} />
          : pestana === "miembros" ? <Miembros />
          : pestana === "pagos" ? <Pagos avisar={avisar} />
          : <ParaVender avisar={avisar} />}

        {!puerta && (
          <p className="inf-pie">
            ¿Cómo entras la primera vez? <button type="button" className="inf-enlace" onClick={() => { setPuerta(true); window.scrollTo({ top: 0 }); }}>Mira la pantalla de bienvenida</button>
          </p>
        )}
      </main>

      <div className={`inf-toast${aviso ? " inf-toast--visible" : ""}`} role="status" aria-live="polite">{aviso}</div>
    </div>
  );
}
